'use client'

import { Card, Cell, Divider, InputText, Label, Radio } from '@vinted/web-ui'
import { useCallback, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import RequiredValidation from '../RequiredValidation'
import {
  ProIntegrationsApplicationFormApiImplementation,
  ProIntegrationsApplicationFormField,
} from '../constants'
import { ProIntegrationsApplicationFormModel } from '../types'

const RadioCell = ({
  title,
  value,
}: {
  title: string
  value: ProIntegrationsApplicationFormApiImplementation
}) => {
  const {
    setValue,
    formState: { errors },
    clearErrors,
  } = useFormContext<ProIntegrationsApplicationFormModel>()
  const currentValue = useWatch<
    ProIntegrationsApplicationFormModel,
    ProIntegrationsApplicationFormField.ApiImplementation
  >({ name: ProIntegrationsApplicationFormField.ApiImplementation })

  const isChecked = currentValue === value

  const handleOnClick = useCallback(() => {
    setValue(ProIntegrationsApplicationFormField.ApiImplementation, value)
    clearErrors(ProIntegrationsApplicationFormField.ApiImplementation)
  }, [setValue, clearErrors, value])

  return (
    <Cell
      type={Cell.Type.Navigating}
      styling={Cell.Styling.Wide}
      title={title}
      role="radio"
      aria={{
        'aria-checked': isChecked,
      }}
      onClick={handleOnClick}
      suffix={
        <Radio
          name={ProIntegrationsApplicationFormField.ApiImplementation + value}
          checked={isChecked}
          testId={`pro-integrations-application-form-api-implementation-${value}`}
          onChange={() => undefined}
          aria={{
            'aria-label': title,
          }}
        />
      }
    >
      <RequiredValidation
        error={errors[ProIntegrationsApplicationFormField.ApiImplementation]}
        testId={`pro-integrations-application-form-api-implementation-${value}-validation`}
      />
    </Cell>
  )
}

const ApiImplementation = () => {
  const translate = useTranslate('business.pro_integrations_application.form.api_implementation')
  const {
    register,
    formState: { errors },
  } = useFormContext<ProIntegrationsApplicationFormModel>()
  const currentValue = useWatch<
    ProIntegrationsApplicationFormModel,
    ProIntegrationsApplicationFormField.ApiImplementation
  >({ name: ProIntegrationsApplicationFormField.ApiImplementation })

  useEffect(() => {
    register(ProIntegrationsApplicationFormField.ApiImplementation, { required: true })
  }, [register])

  return (
    <Card>
      <SeparatedList separator={<Divider />}>
        <>
          <Label styling={Label.Styling.Wide} type={Label.Type.Leading} text={translate('title')} />
          <RadioCell
            title={translate('options.self')}
            value={ProIntegrationsApplicationFormApiImplementation.Self}
          />
        </>
        <RadioCell
          title={translate('options.third_party')}
          value={ProIntegrationsApplicationFormApiImplementation.ThirdParty}
        />
        {currentValue === ProIntegrationsApplicationFormApiImplementation.ThirdParty && (
          <InputText
            {...register(ProIntegrationsApplicationFormField.ThirdParty, {
              required: currentValue === ProIntegrationsApplicationFormApiImplementation.ThirdParty,
            })}
            styling={InputText.Styling.Wide}
            title={translate('third_party.title')}
            placeholder={translate('third_party.placeholder')}
            validation={
              <RequiredValidation error={errors[ProIntegrationsApplicationFormField.ThirdParty]} />
            }
            testId="pro-integrations-application-form-third-party"
          />
        )}
      </SeparatedList>
    </Card>
  )
}

export default ApiImplementation
